import { useCallback, useEffect, useState } from 'react'
import { CreditCard, Loader2, Save } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import {
  fetchPaymentGatewaySettings,
  updatePaymentGatewaySettings,
} from '@/lib/api/paymentGateway'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { cn } from '@/lib/utils'

const inputClass = cn(
  'h-9 w-full rounded-lg border border-border/80 bg-background px-2.5 text-sm',
  'focus:outline-none focus:ring-2 focus:ring-ring',
)

const PROVIDERS = [
  { value: 'none', label: 'Disabled' },
  { value: 'stub', label: 'Stub (dev/test)' },
  { value: 'telr', label: 'Telr' },
  { value: 'paytabs', label: 'PayTabs' },
]

function settingsToForm(settings) {
  return {
    enabled: Boolean(settings?.enabled),
    provider: settings?.provider ?? 'none',
    test_mode: settings?.test_mode !== false,
    merchant_id: settings?.merchant_id ?? '',
    secret_key: '',
    booking_deposit_enabled: Boolean(settings?.booking_deposit_enabled),
    booking_deposit_percent: settings?.booking_deposit_percent ?? 20,
  }
}

export function PaymentGatewaySettingsPanel({ className = '' }) {
  const { activeBranchId } = useBranch()
  const [form, setForm] = useState(null)
  const [secretConfigured, setSecretConfigured] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const settings = await fetchPaymentGatewaySettings()
      setForm(settingsToForm(settings))
      setSecretConfigured(Boolean(settings?.secret_key_configured))
    } catch (err) {
      setForm(null)
      setError(apiErrorMessage(err, 'Unable to load payment gateway settings.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load, activeBranchId])

  const handleSave = async (event) => {
    event.preventDefault()
    if (!form) return

    setSaving(true)
    setError('')
    setMessage('')

    try {
      const payload = { ...form }
      if (!payload.secret_key) {
        delete payload.secret_key
      }
      const updated = await updatePaymentGatewaySettings(payload)
      setForm(settingsToForm(updated))
      setSecretConfigured(Boolean(updated?.secret_key_configured))
      setMessage('Payment gateway settings saved.')
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to save payment gateway settings.'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className={cn('flex items-center gap-2 rounded-2xl border border-border/60 bg-card px-4 py-6 text-sm text-muted-foreground', className)}>
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading payment gateway…
      </div>
    )
  }

  if (!form) {
    return (
      <div className={cn('rounded-2xl border border-red-200/80 bg-red-50/80 px-4 py-3 text-sm text-red-700', className)}>
        {error || 'Payment gateway settings unavailable.'}
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSave}
      className={cn('rounded-2xl border border-border/60 bg-card p-4 shadow-luxury-sm', className)}
    >
      <div className="mb-3 flex items-center gap-2">
        <CreditCard className="h-4 w-4 text-primary" aria-hidden />
        <h3 className="text-sm font-semibold">Payment gateway</h3>
      </div>

      <div className="space-y-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))}
          />
          Enable gateway for this branch
        </label>

        <label className="block space-y-1">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Provider</span>
          <select
            className={inputClass}
            value={form.provider}
            onChange={(event) => setForm((current) => ({ ...current, provider: event.target.value }))}
          >
            {PROVIDERS.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.test_mode}
            onChange={(event) => setForm((current) => ({ ...current, test_mode: event.target.checked }))}
          />
          Test mode (no live charges)
        </label>

        <label className="block space-y-1">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Merchant ID</span>
          <input
            className={inputClass}
            value={form.merchant_id}
            onChange={(event) => setForm((current) => ({ ...current, merchant_id: event.target.value }))}
          />
        </label>

        <label className="block space-y-1">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Secret key {secretConfigured ? '(configured — leave blank to keep)' : ''}
          </span>
          <input
            type="password"
            className={inputClass}
            value={form.secret_key}
            onChange={(event) => setForm((current) => ({ ...current, secret_key: event.target.value }))}
            placeholder={secretConfigured ? '••••••••' : 'Enter secret key'}
          />
        </label>

        <div className="rounded-xl border border-border/50 bg-muted/20 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Online booking deposit</p>
          <label className="mt-2 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.booking_deposit_enabled}
              onChange={(event) => setForm((current) => ({
                ...current,
                booking_deposit_enabled: event.target.checked,
              }))}
            />
            Capture deposit on confirmed online bookings
          </label>
          <label className="mt-2 block space-y-1">
            <span className="text-xs text-muted-foreground">Deposit % of service total</span>
            <input
              type="number"
              min="0"
              max="100"
              className={inputClass}
              value={form.booking_deposit_percent}
              onChange={(event) => setForm((current) => ({
                ...current,
                booking_deposit_percent: Number(event.target.value),
              }))}
            />
          </label>
        </div>
      </div>

      {message ? <p className="mt-3 text-xs text-emerald-700">{message}</p> : null}
      {error ? <p className="mt-3 text-xs text-red-700">{error}</p> : null}

      <Button type="submit" size="sm" className="mt-4" disabled={saving}>
        {saving ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-2 h-3.5 w-3.5" />}
        Save gateway settings
      </Button>
    </form>
  )
}
